import {Input} from 'antd'
import {
  categories_createSubcategory_api,
  categories_deleteSubcategory_api,
  categories_editSubcategory_api,
  categories_getSubcategoriesByOwner_api,
  categories_getSubcategoryById_api,
} from 'apis'
import {getId, getType} from 'apis/model/base'
import _ from 'lodash'
import withTranslate from 'modules/local/withTranslate'
import React from 'react'
import {branch, compose, fromRenderProps, mapProps, pure, renderNothing,} from 'recompose'
import {SubCategoryTypes} from 'views/AppLocals/factory/createEntityManager'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from 'views/AppLocals/Organization/OrganizationContainerContext'
import CRUD from 'views/Shared/enums/CRUD'
import WorkspaceContext from '../WorkspaceContext'
import {renderSubCategory} from './renderSubCategory'
import SubCategoriesContainer from './SubCategoriesContainer'
import {SubCategoriesSingleSelect} from './SubCategoriesSelect'
import {
    firstLetterUppercase,
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces, noNumber, noSpecialChars,
    required,
    runRules
} from "../../../components/form/ValidateForm";

const formSchema = {
    parent_id: {
        title: 'parent of subcategory',
        children: [
            {
                name: 'parent_id',
                component: ({name, form, value, onChange}) => {
                    const defaultValue = form?.values?.parent_id
                    return (
                        <OrganizationContainerContext.Consumer>
                            {({organization}) => (
                                <SubCategoriesSingleSelect
                                    {...{
                                        name,
                                        value,
                                        onChange,
                                        defaultValue,
                                    }}
                                    className="custom-parent-select-subcategory"
                                    type={SubCategoryTypes.ORGANIZATION.value}
                                    owner_id={getId(organization)}
                                    owner_type={getType(organization)}
                                />
                            )}
                        </OrganizationContainerContext.Consumer>
                    )
                },
            },
        ],
    },
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'title',
                    showCount: true,
                    maxLength: 120,
                }),
            },
        ],
    },
    description: {
        title: 'description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 3,
                    placeholder: 'description',
                    showCount: true,
                    maxLength: 500,
                    Component: Input.TextArea,
                }),
            },
        ],
    },
}

const validateTitle = (title, t) => {
    const rules = [
        required(t),
        lengthBetween(1, 120, t),
        noNumber(t),
        noSpecialChars(t),
        firstLetterUppercase(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ]
    const error = runRules(title, rules)
    return error ? { title: error } : {}
}

const validateDescription = (description, t) => {
    const rules = [
        lengthBetween(1, 500, t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ]
    const error = runRules(description, rules)
    return error ? { description: error } : {}
}

const validate = (t) => (values) => {
    const {
        title,
        description
    } = values ?? {}

    const errorTitle = validateTitle(title, t)
    const errorDescription = validateDescription(description, t)

    return {
        ...errorTitle,
        ...errorDescription,
    }
}

export const OrganizationSubCategories = compose(
    withTranslate,
    mapProps(({owner_id, owner_type, translate, ...props}) => {
        const subcategory_type = SubCategoryTypes.ORGANIZATION.value

        return {
            ...props,
            owner_id,
            owner_type,
            title: 'subcategories organization',
            filter_actions: {
                sortBy: 'sort-by',
                keyword: 'keyword',
            },
            fetchItems: {
                apiInfo: categories_getSubcategoriesByOwner_api,
                query: bindQueryParams([
                    {
                        id: owner_id,
                    },
                    {type: owner_type},
                    {subcategory_type},
                ]),
            },
            renderItem: renderSubCategory,
            createFormSchema: (action) => {
                switch (action) {
                    case CRUD.CREATE:
                    case CRUD.UPDATE:
                        return [
                            [formSchema.parent_id, formSchema.title, formSchema.description],
                            validate(translate),
                        ]
                    default:
                        break
                }
                return []
            },
            configs: {
                fetchEntity: (id) => ({
                    apiInfo: categories_getSubcategoryById_api,
                    query: bindQueryParam({
                        id,
                    }),
                }),
                create: {
                    apiInfo: categories_createSubcategory_api,
                    query: bindQueryParams([
                        {
                            id: owner_id,
                        },
                        {type: owner_type},
                    ]),
                    values: {
                        subcategory_type,
                    },
                },
                update: {
                    apiInfo: categories_editSubcategory_api,
                },
                delete: {
                    apiInfo: categories_deleteSubcategory_api,
                },
                modal: {
                    createTitle: 'create subcategories organization',
                    updateTitle: 'update subcategories organization',
                    renderTitle: (title) => (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {typeof title === "string" ? translate(title) : title}
                        </div>
                    ),
                },
            },
        }
    })
)(SubCategoriesContainer)

export default compose(
    fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
        owner_id: getId(organization),
        owner_type: getType(organization),
    })),
    withTranslate,
    branch(
        ({owner_id, owner_type}) => _.isEmpty(owner_id) || _.isEmpty(owner_type),
        renderNothing
    ),
    pure
)(OrganizationSubCategories)
